import type { WpNextJsTools as ServerWpNextJsTools } from './api';
import myLoader from '../utils/imageLoader.browser';

type Config = ConstructorParameters<typeof ServerWpNextJsTools>[0];

const serverOnly = (method: string): Error =>
  new Error(
    `[WpTools] ${method}() só roda no servidor (getStaticProps, getStaticPaths, scripts de build). ` +
      'No navegador o pacote carrega a entrada "browser", que não traz cliente GraphQL, cache em disco ' +
      'nem pipeline de imagens.'
  );

/**
 * `WpNextJsTools` da entrada "browser" do pacote (TD-4 em docs/tech-debts.md).
 *
 * Os consumidores criam a instância no mesmo módulo que exporta o `myLoader`
 * (`export const myLoader = wpTools.getMyLoader()`), e esse módulo é importado
 * por componentes — então o construtor roda no navegador. A versão de servidor
 * arrastava para o cliente urql, o cache em disco (`crypto`, `fs`, `path`), o
 * pipeline de imagens (`image-size`, `worker_threads`) e `Buffer`.
 *
 * Aqui só existe o que o navegador usa: `getMyLoader()`. Os demais métodos
 * públicos existem para o import não quebrar, e lançam se forem chamados. O
 * construtor não valida as variáveis de ambiente: no navegador nenhuma delas é
 * usada, e a validação do build continua na versão de servidor.
 */
export class WpNextJsTools {
  projectBasePath: string;
  isProduction: boolean;
  enableDebugExchange: boolean;
  private readonly IMGPROXY_PORT: string;

  constructor(config: Config) {
    this.projectBasePath = config.basePath || '';
    this.isProduction = config.isProduction || false;
    this.enableDebugExchange = config.enableDebugExchange || false;
    this.IMGPROXY_PORT = config.IMGPROXY_PORT;
  }

  public getMyLoader() {
    return (props: Parameters<typeof myLoader>[0]): ReturnType<typeof myLoader> => {
      return myLoader({
        ...props,
        isProduction: this.isProduction,
        basePath: this.projectBasePath,
        imgproxyPort: this.IMGPROXY_PORT,
      });
    };
  }

  public async downloadImageSaveLocal(_src: string): Promise<string> {
    throw serverOnly('downloadImageSaveLocal');
  }

  public async changeDataByLocal(_obj: any): Promise<any> {
    throw serverOnly('changeDataByLocal');
  }

  public clearQueryCache(): void {
    throw serverOnly('clearQueryCache');
  }

  public async queryServerAll(..._args: unknown[]): Promise<any> {
    throw serverOnly('queryServerAll');
  }

  public async queryServer(..._args: unknown[]): Promise<any> {
    throw serverOnly('queryServer');
  }
}
